import axios from 'axios';
import cookie from 'js-cookie';

// Get the API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Create a new user with business assignment
 * @param {Object} payload - UserWithBusinessDTO
 * @returns {Promise<Object>} - Created user data
 */
export const createUserWithBusiness = async (payload) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/user/create-with-business`, payload, {
            withCredentials: true,
            headers: {
                'Content-Type': 'application/json',
                'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error creating user with business:', error);
        throw error;
    }
};

/**
 * Fetch businesses owned by the current user's business.
 * Automatically scoped to the logged-in user's business from session.
 * @returns {Promise<Array>} - Array of business objects
 */
export const fetchOwnedBusinesses = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/business/owned-by-me`, {
            withCredentials: true,
            headers: {
                'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching owned businesses:', error);
        throw error;
    }
};

/**
 * Fetch all users with their business and role information
 * This calls the backend endpoint to get all users across the organization
 * @returns {Promise<Array>} - Array of user objects with business and role information
 */
export const fetchAllUsers = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/user/get-users-with-business`, {
            withCredentials: true,
            headers: {
                'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
            },
        });

        // Transform the nested backend response to the flat structure expected by the frontend
        const transformedData = response.data.map((item) => ({
            userId: item.user.userId,
            firstName: item.user.firstName,
            lastName: item.user.lastName,
            email: item.user.email,
            phoneNumber: item.user.phoneNumber,
            accountStatus: item.user.accountStatus,
            role: item.role,
            businessMembershipId: item.businessMembershipId,
            businessName: item.business.businessName,
            businessId: item.business.businessId,
            businessStreetAddress: item.business.streetAddress,
            businessCity: item.business.city,
            businessProvince: item.business.province,
            businessPostalCode: item.business.postalCode,
            businessCountry: item.business.country,
            businessAddress: [
                item.business.streetAddress,
                item.business.city,
                item.business.province,
                item.business.postalCode,
                item.business.country,
            ]
                .filter(Boolean)
                .join(', '),
            businessPhone: item.business.phoneNumber,
            businessEmail: item.business.email,
            isInternalUser: item.isInternalUser,
        }));

        return transformedData;
    } catch (error) {
        console.error('Error fetching all users:', error);
        throw error;
    }
};

/**
 * Update a user's information
 * @param {string} userId - The user ID to update
 * @param {Object} userPayload - UserDTO payload
 * @returns {Promise<Object>} - Updated user data
 */
export const updateUser = async (userId, userPayload) => {
    try {
        const response = await axios.put(
            `${API_BASE_URL}/user/update-user/${userId}`,
            userPayload,
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error updating user:', error);
        throw error;
    }
};

/**
 * Move an external user's exact business membership to an existing or newly created business.
 * @param {string} userId - The user ID whose membership is moving
 * @param {Object} payload - { sourceMembershipId, targetBusinessId } or
 *                           { sourceMembershipId, newBusiness }
 * @returns {Promise<Object>} - Updated UserWithBusinessDTO
 */
export const moveExternalBusinessMembership = async (userId, payload) => {
    try {
        const response = await axios.put(
            `${API_BASE_URL}/user/${userId}/external-business-membership/move`,
            payload,
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error moving external business membership:', error);
        throw error;
    }
};

/**
 * Update a business's information
 * @param {string} businessId - The business ID to update
 * @param {Object} businessPayload - BusinessDTO payload
 * @returns {Promise<Object>} - Updated business data
 */
export const updateBusiness = async (businessId, businessPayload) => {
    try {
        const response = await axios.put(
            `${API_BASE_URL}/business/update-business/${businessId}`,
            businessPayload,
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error updating business:', error);
        throw error;
    }
};
